import React, { memo } from 'react';
import { useParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { UserRole } from 'context';
import { LoadingIndicator } from 'atoms';
import { authState, useLessonWhiteboard } from 'core';

import { WhiteboardEditor } from './WhiteboardEditor';
import { WhiteboardViewer } from './WhiteboardViewer';
import { WhiteboardStartPanel } from './WhiteboardStartPanel';

interface ILessonWhiteboardContainer {
  lessonId: string;
}

/**
 * Role dispatch for the lesson "Доска" tab (spec 0010 §6):
 *   TEACHER            → editor when a board exists, start panel otherwise;
 *   STUDENT/METHODIST  → read-only viewer when the server exposes a board
 *                        (the backend gates student access on lesson end),
 *                        empty-state text otherwise.
 */
export const LessonWhiteboardContainer = memo(({ lessonId }: ILessonWhiteboardContainer) => {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId: string }>();
  const { active_role } = useReactiveVar(authState);

  const { whiteboard, loading, error, refetchWhiteboard } = useLessonWhiteboard(lessonId);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <EmptyText>{t('whiteboard.errors.loadFailed')}</EmptyText>;
  }

  if (active_role === UserRole.TEACHER) {
    return whiteboard ? (
      // key: switching to another linked board must remount the editor so the
      // canvas re-initializes from the new board's scene.
      <WhiteboardEditor
        key={whiteboard.id}
        board={whiteboard}
        refetchWhiteboard={refetchWhiteboard}
      />
    ) : (
      <WhiteboardStartPanel
        lessonId={lessonId}
        groupId={groupId || ''}
        onBoardReady={refetchWhiteboard}
      />
    );
  }

  return whiteboard ? (
    <WhiteboardViewer board={whiteboard} />
  ) : (
    <EmptyText>{t('whiteboard.empty')}</EmptyText>
  );
});

const EmptyText = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;
